// client/src/pages/PapabileDuJourPage.js
import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import PageWrapper from '../components/PageWrapper';
import '../styles/PapabileDuJourPage.css';

// Composants SVG inline
const IconCalendar = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const IconQuote = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21c3.33-3.33 5-6 5-8 0-3-1-3-2-3-1.67 0-2 1-2 2 0 1.67 1 3 3 3-2 1-6 1-6-4 0-3.67 1.67-6 5-6 5 0 5 6 5 6 0 5-3 9-8 10zm18 0c-3.33-3.33-5-6-5-8 0-3 1-3 2-3 1.67 0 2 1 2 2 0 1.67-1 3-3 3 2 1 6 1 6-4 0-3.67-1.67-6-5-6-5 0-5 6-5 6 0 5 3 9 8 10z"></path>
  </svg>
);

const IconCrown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14"></path>
  </svg>
);

const IconArrowLeft = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"></line>
    <polyline points="12 19 5 12 12 5"></polyline>
  </svg>
);

const IconArrowRight = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"></line>
    <polyline points="12 5 19 12 12 19"></polyline>
  </svg>
);

const PapabileDuJourPage = () => {
  const { slug } = useParams();
  const [papabile, setPapabile] = useState(null);
  const [recentPapabiles, setRecentPapabiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [prevSlug, setPrevSlug] = useState(null);
  const [nextSlug, setNextSlug] = useState(null);
  const [imageError, setImageError] = useState(false);
  
  // Image de fallback pour les cardinaux sans photo
  const defaultImage = '/images/cardinal-default.png';
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setImageError(false);
        
        // Récupérer le papabile du jour (actuel ou par slug)
        let response;
        if (slug) {
          response = await axios.get(`/api/papabile-jour/slug/${slug}`);
        } else {
          response = await axios.get('/api/papabile-jour/current');
        }
        
        setPapabile(response.data);
        
        // Récupérer les papabiles récents pour l'historique
        const recentResponse = await axios.get('/api/papabile-jour/recent?limit=10');
        setRecentPapabiles(recentResponse.data);
        
        // Déterminer les liens de navigation précédent/suivant
        if (recentResponse.data.length > 0) {
          const currentIndex = recentResponse.data.findIndex(p => p.slug === (slug || response.data.slug));
          
          if (currentIndex > 0) {
            setNextSlug(recentResponse.data[currentIndex - 1].slug);
          } else {
            setNextSlug(null);
          }
          
          if (currentIndex < recentResponse.data.length - 1) {
            setPrevSlug(recentResponse.data[currentIndex + 1].slug);
          } else {
            setPrevSlug(null);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement du papabile du jour:', err);
        setError('Erreur lors du chargement des données');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [slug]);
  
  // Utiliser la route API pour les images avec le bon content-type
  const getApiImageUrl = (originalUrl) => {
    if (!originalUrl || imageError) return defaultImage;
    
    // Extraire le nom du fichier de l'URL
    const filename = originalUrl.split('/').pop().split('?')[0];
    
    // Ajouter un timestamp pour éviter le cache du navigateur
    const timestamp = new Date().getTime();
    return `/api/images/${filename}?t=${timestamp}`;
  };
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = () => {
    console.log("Erreur de chargement d'image, utilisation de l'image par défaut");
    setImageError(true);
  };
  
  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    return format(new Date(dateString), 'EEEE d MMMM yyyy', { locale: fr });
  };
  
  if (loading) return <div className="loading">Chargement du papabile du jour...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!papabile) return <div className="error">Papabile du jour non trouvé</div>;
  
  return (
    <PageWrapper pageType="papabile-jour">
      <div className="papabile-jour-page">
        <div className="papabile-jour-header">
          <h1>Le Papabile du Jour</h1>
          <div className="papabile-date">
            <IconCalendar />
            <span>{formatDate(papabile.dateAffichage)}</span>
          </div>
        </div>
        
        <div className="papabile-jour-content">
          <div className="papabile-spotlight">
            <div className="spotlight-photo-container">
              <div className="spotlight-frame">
                <img 
                  src={getApiImageUrl(papabile.cardinalId.photo)} 
                  alt={papabile.cardinalId.nomComplet} 
                  className="spotlight-photo" 
                  onError={handleImageError}
                />
                
                {papabile.cardinalId.statutVotant === 'OUI' && (
                  <div className="spotlight-badge" title="Électeur au conclave">
                    <IconCrown />
                  </div>
                )}
              </div>
            </div>
            
            <div className="spotlight-info">
              <h2 className="spotlight-name">{papabile.cardinalId.nomComplet}</h2>
              <p className="spotlight-title">{papabile.cardinalId.titre || papabile.cardinalId.ordre}</p>
              
              <div className="spotlight-metadata">
                <span className="spotlight-metadata-item">{papabile.cardinalId.age} ans</span>
                <span className="spotlight-metadata-item">{papabile.cardinalId.paysService}</span>
                <span className="spotlight-metadata-item">{papabile.cardinalId.continent}</span>
              </div>
              
              {papabile.citation && (
                <div className="spotlight-quote">
                  <div className="quote-icon">
                    <IconQuote />
                  </div>
                  <blockquote>
                    {papabile.citation}
                  </blockquote>
                </div>
              )}
              
              {papabile.commentaire && (
                <div className="spotlight-comment">
                  <p>{papabile.commentaire}</p>
                </div>
              )}
              
              <div className="spotlight-actions">
                <Link to={`/cardinaux/${papabile.cardinalId.slug}`} className="view-profile-button">
                  Voir le profil complet
                </Link>
              </div>
            </div>
          </div>
          
          <div className="papabile-navigation">
            {prevSlug && (
              <Link to={`/papabile-du-jour/${prevSlug}`} className="nav-button prev-button">
                <IconArrowLeft />
                <span>Précédent</span>
              </Link>
            )}
            
            <Link to="/papabile-du-jour" className="nav-button current-button">
              Aujourd'hui
            </Link>
            
            {nextSlug && (
              <Link to={`/papabile-du-jour/${nextSlug}`} className="nav-button next-button">
                <span>Suivant</span>
                <IconArrowRight />
              </Link>
            )}
          </div>
          
          <div className="papabile-recent">
            <h3>Papabili récents</h3>
            
            <div className="recent-grid">
              {recentPapabiles.slice(0, 6).map(recent => (
                <Link 
                  to={`/papabile-du-jour/${recent.slug}`} 
                  key={recent._id}
                  className={`recent-card ${recent.slug === papabile.slug ? 'active' : ''}`}
                >
                  <div className="recent-photo">
                    <img 
                      src={getApiImageUrl(recent.cardinalId.photo)} 
                      alt={recent.cardinalId.nomComplet} 
                      onError={() => {/* Utiliser l'image par défaut */}}
                    />
                  </div>
                  <div className="recent-info">
                    <div className="recent-name">{recent.cardinalId.nomComplet}</div>
                    <div className="recent-date">{format(new Date(recent.dateAffichage), 'dd/MM/yyyy')}</div>
                  </div>
                </Link>
              ))}
            </div>
            
            <div className="archive-link-container">
              <Link to="/papabile-du-jour/archive" className="archive-link">
                Voir tous les papabili du jour
              </Link>
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default PapabileDuJourPage;